import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { TextField } from '@/components/forms';
import { routes } from '@/config/routes';
import { ApiError } from '@/lib/api/errors';
import { tokenStore } from '@/lib/api/token-store';
import { formatPhoneUS } from '@/lib/utils/format';
import { useAuth } from '@/providers/auth-provider';

import {
  CODE_ENTRY_COPY,
  EDIT_PROFILE_COPY,
  PHONE_CHANGE_COPY,
  PROFILE_ERROR_CODES,
} from '../../constants';
import {
  profileKeys,
  useResendPhoneOtp,
  useSendCurrentPhoneOtp,
  useVerifyPhoneOtp,
} from '../../hooks';
import type { CodeDispatch } from '../../schemas';
import { OtpStepModal } from '../otp-step-modal/otp-step-modal';

import { NewPhoneStep } from './new-phone-step';

type Step =
  | { kind: 'current' }
  | { kind: 'current-code'; dispatch: CodeDispatch }
  | { kind: 'new'; initial?: { phone: string; error: string } }
  | { kind: 'new-code'; dispatch: CodeDispatch; phone: string };

export type PhoneChangeFlowProps = {
  /** The Registered phone number from the saved identity Edit Profile was opened with. */
  currentPhone: string;
  /** Back to Edit Profile with nothing applied: Cancel / ✕ on any step, and the expiry exit. */
  onClose: () => void;
};

/**
 * The Phone change (US-117543 §E–H): verify the current number → enter a new one → verify it.
 * Mounted by `<EditProfileModal>` only while open, so it stacks above Edit Profile and closing it
 * discards every step's state. Success ends every session server-side; the client follows suit.
 */
export function PhoneChangeFlow({ currentPhone, onClose }: PhoneChangeFlowProps) {
  const [step, setStep] = useState<Step>({ kind: 'current' });
  const sendCurrent = useSendCurrentPhoneOtp();
  const verifyCurrent = useVerifyPhoneOtp('current');
  const resendCurrent = useResendPhoneOtp('current');
  const verifyNew = useVerifyPhoneOtp('new');
  const resendNew = useResendPhoneOtp('new');
  const toast = useToast();
  const queryClient = useQueryClient();
  const { clearSession } = useAuth();
  const navigate = useNavigate();
  const { control } = useForm({ values: { phone: formatPhoneUS(currentPhone) } });

  // One exit for the code steps' timer / server codes and Step 3's 403 (spec: Phone change).
  const expire = useCallback(() => {
    toast.warning({ title: CODE_ENTRY_COPY.EXPIRED });
    onClose();
  }, [toast, onClose]);

  // Grilling Q17: tokens first, so no in-flight poll finds a live credential and toasts an expiry.
  const signOutEverywhere = () => {
    tokenStore.clear();
    queryClient.removeQueries({ queryKey: profileKeys.all });
    clearSession();
    navigate(routes.login);
  };

  switch (step.kind) {
    case 'current-code':
      return (
        <OtpStepModal
          key={step.dispatch.referenceId}
          dispatch={step.dispatch}
          verify={(referenceId, otp) => verifyCurrent.mutateAsync({ referenceId, otp })}
          resend={(referenceId) => resendCurrent.mutateAsync(referenceId)}
          onVerified={() => setStep({ kind: 'new' })}
          onExpired={expire}
          onClose={onClose}
        />
      );
    case 'new':
      return (
        <NewPhoneStep
          currentPhone={currentPhone}
          initial={step.initial}
          onSent={(dispatch, phone) => setStep({ kind: 'new-code', dispatch, phone })}
          onExpired={expire}
          onClose={onClose}
        />
      );
    case 'new-code':
      return (
        <OtpStepModal
          key={step.dispatch.referenceId}
          dispatch={step.dispatch}
          verify={(referenceId, otp) => verifyNew.mutateAsync({ referenceId, otp })}
          resend={(referenceId) => resendNew.mutateAsync(referenceId)}
          onVerified={signOutEverywhere}
          onVerifyError={(error) => {
            if (!(error instanceof ApiError)) return false;
            // verify-new's 403: the session holds no new number any more — the expiry exit.
            if (error.code === PROFILE_ERROR_CODES.INVALID_SESSION) {
              expire();
              return true;
            }
            if (error.code !== PROFILE_ERROR_CODES.PHONE_ALREADY_EXISTS) return false;
            setStep({
              kind: 'new',
              initial: { phone: step.phone, error: PHONE_CHANGE_COPY.TAKEN },
            });
            return true;
          }}
          onExpired={expire}
          onClose={onClose}
        />
      );
    default:
      return (
        <Modal
          open
          size="sm"
          onClose={onClose}
          title={PHONE_CHANGE_COPY.STEP1_TITLE}
          subtitle={PHONE_CHANGE_COPY.STEP1_SUBTITLE}
          primaryText="Send Code"
          // A failure is toasted by <MutationToaster>; staying on Step 1 is just not advancing.
          onPrimary={() =>
            sendCurrent.mutate(undefined, {
              onSuccess: (dispatch) => setStep({ kind: 'current-code', dispatch }),
            })
          }
          loading={sendCurrent.isPending}
        >
          <TextField
            name="phone"
            control={control}
            label={EDIT_PROFILE_COPY.PHONE}
            prefix="+1"
            required
            disabled
          />
        </Modal>
      );
  }
}
